import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { dump, load } from 'js-yaml';
// material
import { Typography, Grid, TextField } from '@material-ui/core';
// icons

const scriptPlaceholder = `- apt install -y iperf
- echo "boot" > /boot.txt
- docker run nginx`;

const initScriptDefaultHelperText =
  'These commands run only once during the first time the vm boots up.';

const bootScriptDefaultHelperText = 'These commands run every time the vm reboots.';

function scriptToJson(scripts) {
  // parse string yaml into json object
  try {
    return load(scripts);
  } catch (error) {
    console.error('failed to parse startup script to json', error);
    return null;
  }
}

function scriptToYaml(scripts) {
  // converts script object to yaml
  if (!scripts) {
    return '';
  }
  try {
    return dump(scripts);
  } catch (error) {
    console.error('failed to parse startup script to yaml', error);
    return null;
  }
}

InitScripts.propTypes = {
  setNextButtonDisabled: PropTypes.func,
  rackInitScript: PropTypes.string,
  rackBootScript: PropTypes.string,
  updateRackInitScript: PropTypes.func,
  updateRackBootScript: PropTypes.func
};

export default function InitScripts({
  setNextButtonDisabled,
  rackInitScript,
  rackBootScript,
  updateRackInitScript,
  updateRackBootScript
}) {
  // helper functions to extract values from the rack object for initialization
  const parseInitScriptFromRack = () => scriptToYaml(rackInitScript);
  const parseBootScriptFromRack = () => scriptToYaml(rackBootScript);

  const [initScript, updateInitScript] = useState(parseInitScriptFromRack);
  const [bootScript, updateBootScript] = useState(parseBootScriptFromRack);
  const [initScriptError, updateInitScriptError] = useState(false);
  const [bootScriptError, updateBootScriptError] = useState(false);
  const [initScriptHelperText, updateInitScriptHelperText] = useState(initScriptDefaultHelperText);
  const [bootScriptHelperText, updateBootScriptHelperText] = useState(bootScriptDefaultHelperText);

  const updateRackScript = (scriptType, script) => {
    if (scriptType === 'initScript') {
      updateRackInitScript(script);
    } else {
      updateRackBootScript(script);
    }
  };

  const validateScript = (s, type) => {
    const defaultScriptErrorMessage =
      "Invalid script format. Separate each command with a '-' followed by a space";
    const script = scriptToJson(s);
    if (s !== '' && (!script || !s.startsWith('- '))) {
      // failed to parse or script didn't start with a '- ', set error messages on the text boxes
      if (type === 'initScript') {
        updateInitScriptError(true);
        updateInitScriptHelperText(defaultScriptErrorMessage);
      } else {
        updateBootScriptError(true);
        updateBootScriptHelperText(defaultScriptErrorMessage);
      }
      updateRackScript(type, null);
      return;
    }
    if (s === '') {
      updateRackScript(type, null);
    } else {
      // successfully parsed script
      updateRackScript(type, script);
    }
    if (type === 'initScript') {
      updateInitScriptError(false);
      updateInitScriptHelperText(initScriptDefaultHelperText);
    } else {
      updateBootScriptError(false);
      updateBootScriptHelperText(bootScriptDefaultHelperText);
    }
  };

  useEffect(() => {
    validateScript(initScript, 'initScript');
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [initScript]);

  useEffect(() => {
    // update rack object when boot script is updated
    validateScript(bootScript, 'bootScript');
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [bootScript]);

  useEffect(() => {
    validateInputs();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  const validateInputs = () => {
    setNextButtonDisabled(false);
  };

  return (
    <Grid container spacing={4} data-testid="init-scripts">
      <Grid item xs={12} sm={6}>
        <Typography variant="overline" sx={{ marginBottom: 2, display: 'block' }}>
          Init Script
        </Typography>
        <TextField
          data-testid="init-script"
          placeholder={scriptPlaceholder}
          multiline
          fullWidth
          rows={5}
          onChange={(event) => {
            updateInitScript(event.target.value);
          }}
          helperText={initScriptHelperText}
          error={initScriptError}
          value={initScript}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Typography variant="overline" sx={{ marginBottom: 2, display: 'block' }}>
          Boot Script
        </Typography>
        <TextField
          data-testid="boot-script"
          placeholder={scriptPlaceholder}
          multiline
          fullWidth
          rows={5}
          onChange={(event) => {
            updateBootScript(event.target.value);
          }}
          error={bootScriptError}
          helperText={bootScriptHelperText}
          value={bootScript}
          aria-label="boot-script-input"
        />
      </Grid>
    </Grid>
  );
}
